import { useUserQuery } from '@/api/queries/useAuth';
import { commentsEntity } from '@/data/entities/comments';
import type { IComment, IMessage } from '@/types';
import { useSyncedChannel } from './useSyncedChannel';

/**
 * Subscribes to real-time comment updates for a comment thread via Pusher.
 * Call alongside useCommentQuery() to get live updates.
 *
 * Uses useSyncedChannel to buffer WebSocket events during in-flight mutations,
 * preventing race conditions between optimistic updates and server echoes.
 */
export function useCommentWebSocket(commentId: number | null, token: string) {
    const { data: userData } = useUserQuery();
    const userId = userData?.id;

    useSyncedChannel<IComment[], IMessage>({
        queryKey: commentsEntity.fetchers.detail.key({ id: commentId ?? 0 }),
        channel: `comments.thread.${commentId}`,
        token,
        enabled: !!commentId,
        events: {
            CommentCreated: {
                skip: e => e.comment.user.id === userId,
                update: (old, e) => {
                    if (!old?.length) return old;
                    const first = old[0];
                    if (!first) return old;
                    const updated = [...old];
                    updated[0] = {
                        ...first,
                        children: [
                            ...first.children,
                            {
                                id: e.comment.id,
                                text: e.comment.text,
                                created_at: e.comment.created_at,
                                updated_at: e.comment.updated_at,
                                user: e.comment.user,
                            },
                        ],
                    };
                    return updated;
                },
            },
        },
    });
}
